/* Copyright (c) Trimble Inc. All rights reserved. */

import { Logger } from '@tcp-base/toolkit';
import { getEnvironmentEndpoints } from '../env.config';
import { JwtToken } from '../models/Token.model';
import type { TokenExchangeRequest } from '../models/Token.model';

/**
 * Authentication Service
 *
 * Handles OAuth2 authorization code to access token exchange using PKCE.
 * Manages the complete token exchange flow including PKCE verification.
 */
export class TokenExchangerServiceImpl {
  /**
   * Exchange authorization code for access tokens
   *
   * @param request Token exchange parameters
   * @returns Promise with token response
   */
  async ExchangeCodeForTokens(request: TokenExchangeRequest): Promise<JwtToken> {
    const { code, clientId, redirectUri, codeVerifier, environment } = request;

    const endpoints = getEnvironmentEndpoints(environment);

    try {
      const response = await fetch(endpoints.TOKEN_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          Accept: 'application/json',
        },
        body: new URLSearchParams({
          grant_type: 'authorization_code',
          client_id: clientId,
          code: code,
          redirect_uri: redirectUri,
          code_verifier: codeVerifier, // PKCE verification
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `Token exchange failed: ${response.status} ${response.statusText} - ${errorText}`,
        );
      }

      const tokens: JwtToken = await response.json();

      // Validate required fields
      if (!tokens.access_token || !tokens.token_type) {
        throw new Error('Invalid token response: missing required fields');
      }

      return tokens;
    } catch (error) {
      Logger.error('TokenExchange: API call failed:', error);
      throw error instanceof Error ? error : new Error('Unknown token exchange error');
    }
  }
}

// Create singleton instance
export const TokenExchangerService = new TokenExchangerServiceImpl();
